import React, { useState } from 'react';



const PROPERTY_AGE_OPTIONS = [
  { value: '', label: 'Any Age' },
  { value: 'new', label: 'New Construction' },
  { value: '1-5', label: '1–5 Years' },
  { value: '5-10', label: '5–10 Years' },
  { value: '10+', label: '10+ Years' },
];

const INSPECTION_STATUS_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'available', label: 'Available Now' },
  { value: 'by_appointment', label: 'By Appointment' },
  { value: 'no_inspections', label: 'No Inspections' },
];

const FacetedFilterPanel = ({
  facetFilters,
  onFacetFiltersChange,
  savedTemplates,
  onSaveTemplate,
  onApplyTemplate,
  onDeleteTemplate,
  mode,
}) => {
  const [showSaveTemplateForm, setShowSaveTemplateForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [priceRangeError, setPriceRangeError] = useState('');

  const isRent = mode === 'rent';
  const priceLabel = isRent ? 'Rent Range (per year)' : 'Price Range';
  const minPlaceholder = isRent ? 'Min rent' : 'Min price';
  const maxPlaceholder = isRent ? 'Max rent' : 'Max price';

  const handleMinPrice = (val) => {
    setPriceRangeError('');
    const min = val;
    const max = facetFilters?.maxPrice;
    if (max && Number(min) > Number(max)) {
      setPriceRangeError('Min cannot exceed max');
    }
    onFacetFiltersChange({ ...facetFilters, minPrice: val });
  };

  const handleMaxPrice = (val) => {
    setPriceRangeError('');
    const min = facetFilters?.minPrice;
    const max = val;
    if (min && Number(max) < Number(min)) {
      setPriceRangeError('Max cannot be less than min');
    }
    onFacetFiltersChange({ ...facetFilters, maxPrice: val });
  };

  const handleSaveTemplate = () => {
    if (!templateName?.trim()) return;
    onSaveTemplate(templateName?.trim(), facetFilters);
    setTemplateName('');
    setShowSaveTemplateForm(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleClearFacets = () => {
    setPriceRangeError('');
    onFacetFiltersChange({
      minPrice: '',
      maxPrice: '',
      propertyAge: '',
      inspectionStatus: '',
    });
  };

  const hasActiveFilters =
    facetFilters?.minPrice ||
    facetFilters?.maxPrice ||
    facetFilters?.propertyAge ||
    facetFilters?.inspectionStatus;

  return (
    <div
      className="rounded-xl p-4 mb-4"
      style={{
        background: 'rgba(13, 27, 42, 0.8)',
        border: '1px solid rgba(0, 212, 255, 0.15)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00D4FF" strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span className="text-sm font-semibold" style={{ color: '#00D4FF' }}>Advanced Filters</span>
          {hasActiveFilters && (
            <span
              className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
              style={{ background: 'rgba(0, 212, 255, 0.25)', color: '#00D4FF' }}
            >
              {[facetFilters?.minPrice || facetFilters?.maxPrice, facetFilters?.propertyAge, facetFilters?.inspectionStatus]?.filter(Boolean)?.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <button
              onClick={handleClearFacets}
              className="text-xs px-2 py-1 rounded-lg transition-all"
              style={{ color: '#FF6B6B', border: '1px solid rgba(255, 107, 107, 0.3)', background: 'rgba(255, 107, 107, 0.08)' }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Price Range */}
        <div>
          <label className="text-xs font-semibold mb-2 block" style={{ color: '#8BA5C0' }}>
            {priceLabel}
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              placeholder={minPlaceholder}
              value={facetFilters?.minPrice || ''}
              onChange={(e) => handleMinPrice(e?.target?.value)}
              className="flex-1 px-3 py-2 rounded-lg text-sm outline-none w-0"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: priceRangeError ? '1px solid rgba(255, 107, 107, 0.6)' : '1px solid rgba(0, 212, 255, 0.2)',
                color: '#E2E8F0',
              }}
            />
            <span className="flex items-center text-xs" style={{ color: '#6B9EC4' }}>–</span>
            <input
              type="number"
              placeholder={maxPlaceholder}
              value={facetFilters?.maxPrice || ''}
              onChange={(e) => handleMaxPrice(e?.target?.value)}
              className="flex-1 px-3 py-2 rounded-lg text-sm outline-none w-0"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: priceRangeError ? '1px solid rgba(255, 107, 107, 0.6)' : '1px solid rgba(0, 212, 255, 0.2)',
                color: '#E2E8F0',
              }}
            />
          </div>
          {priceRangeError && (
            <p className="text-xs mt-1" style={{ color: '#FF6B6B' }}>{priceRangeError}</p>
          )}
        </div>

        {/* Property Age */}
        <div>
          <label className="text-xs font-semibold mb-2 block" style={{ color: '#8BA5C0' }}>Property Age</label>
          <div className="flex flex-wrap gap-1.5">
            {PROPERTY_AGE_OPTIONS?.map((opt) => (
              <button
                key={opt?.value}
                onClick={() => onFacetFiltersChange({ ...facetFilters, propertyAge: opt?.value })}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: facetFilters?.propertyAge === opt?.value ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255,255,255,0.04)',
                  color: facetFilters?.propertyAge === opt?.value ? '#00D4FF' : '#8BA5C0',
                  border: facetFilters?.propertyAge === opt?.value ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {opt?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Inspection Status */}
        <div>
          <label className="text-xs font-semibold mb-2 block" style={{ color: '#8BA5C0' }}>Inspection Status</label>
          <div className="flex flex-wrap gap-1.5">
            {INSPECTION_STATUS_OPTIONS?.map((opt) => (
              <button
                key={opt?.value}
                onClick={() => onFacetFiltersChange({ ...facetFilters, inspectionStatus: opt?.value })}
                className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                style={{
                  background: facetFilters?.inspectionStatus === opt?.value ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255,255,255,0.04)',
                  color: facetFilters?.inspectionStatus === opt?.value ? '#00D4FF' : '#8BA5C0',
                  border: facetFilters?.inspectionStatus === opt?.value ? '1px solid rgba(0, 212, 255, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {opt?.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Saved Filter Templates */}
      <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(0, 212, 255, 0.1)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B9EC4" strokeWidth="2">
              <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
            </svg>
            <span className="text-xs font-semibold" style={{ color: '#6B9EC4' }}>Filter Templates</span>
            {savedTemplates?.length > 0 && (
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-xs font-bold"
                style={{ background: 'rgba(0, 212, 255, 0.2)', color: '#00D4FF' }}
              >
                {savedTemplates?.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {savedTemplates?.length > 0 && (
              <button
                onClick={() => setShowTemplates(v => !v)}
                className="text-xs px-2.5 py-1 rounded-lg transition-all"
                style={{
                  background: showTemplates ? 'rgba(0, 212, 255, 0.15)' : 'rgba(255,255,255,0.04)',
                  color: showTemplates ? '#00D4FF' : '#8BA5C0',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                }}
              >
                {showTemplates ? 'Hide' : 'Show Templates'}
              </button>
            )}
            <button
              onClick={() => { setShowSaveTemplateForm(v => !v); setShowTemplates(false); }}
              className="text-xs px-2.5 py-1 rounded-lg transition-all"
              style={{
                background: saveSuccess ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255,255,255,0.04)',
                color: saveSuccess ? '#00D4FF' : '#8BA5C0',
                border: '1px solid rgba(0, 212, 255, 0.2)',
              }}
            >
              {saveSuccess ? '✓ Saved!' : '+ Save Current'}
            </button>
          </div>
        </div>

        {/* Save Template Form */}
        {showSaveTemplateForm && (
          <div
            className="mt-3 p-3 rounded-lg"
            style={{ background: 'rgba(0, 212, 255, 0.05)', border: '1px solid rgba(0, 212, 255, 0.15)' }}
          >
            <p className="text-xs font-semibold mb-2" style={{ color: '#00D4FF' }}>Save current filters as template</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e?.target?.value)}
                onKeyDown={(e) => e?.key === 'Enter' && handleSaveTemplate()}
                placeholder="e.g. Sydney Buy Under $2M"
                className="flex-1 px-3 py-1.5 rounded-lg text-xs outline-none"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  color: '#E2E8F0',
                }}
                autoFocus
              />
              <button
                onClick={handleSaveTemplate}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{ background: 'rgba(0, 212, 255, 0.2)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.3)' }}
              >
                Save
              </button>
              <button
                onClick={() => { setShowSaveTemplateForm(false); setTemplateName(''); }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#8BA5C0', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Templates List */}
        {showTemplates && savedTemplates?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {savedTemplates?.map((tpl) => (
              <div
                key={tpl?.id}
                className="flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-lg group"
                style={{
                  background: 'rgba(0, 212, 255, 0.07)',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                }}
              >
                <button
                  onClick={() => onApplyTemplate(tpl)}
                  className="text-xs font-medium transition-all"
                  style={{ color: '#8BA5C0' }}
                  title={`Apply: ${tpl?.name}`}
                >
                  {tpl?.name}
                </button>
                <div className="flex items-center gap-0.5 ml-1">
                  <button
                    onClick={() => onApplyTemplate(tpl)}
                    className="p-0.5 rounded transition-all"
                    style={{ color: '#00D4FF' }}
                    title="Apply template"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => onDeleteTemplate(tpl?.id)}
                    className="p-0.5 rounded transition-all opacity-0 group-hover:opacity-100"
                    style={{ color: '#FF6B6B' }}
                    title="Delete template"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {savedTemplates?.length === 0 && !showSaveTemplateForm && (
          <p className="text-xs mt-2" style={{ color: '#4A6580' }}>No saved templates yet. Set filters above and save them for quick reuse.</p>
        )}
      </div>
    </div>
  );
};

export default FacetedFilterPanel;
